import React, { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const getColumnSize = (width) => {
  if (!width) return {};
  if (typeof width === "number") return { width };
  if (typeof width === "string") {
    const trimmed = width.trim();
    if (trimmed.endsWith("%")) {
      return { minWidth: 120 };
    }
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? {} : { width: parsed };
  }
  return {};
};

const AnDataGrid = ({
  rowData,
  columns,
  height,
  rowSelection,
  onSelectionChanged,
  isRowSelectable,
  getRowId,
  className,
  style,
  showSelectionColumn,
  selectionColumnWidth = 48,
  rowHeight = 34,
  getRowClass,
  onRowClicked,
}) => {
  const columnDefs = useMemo(() => {
    const defs = (columns ?? []).map((column) => {
      const size = getColumnSize(column?.width);
      const def = {
        headerName: column.header,
        field: column.field,
        sortable: column.sortable ?? true,
        ...size,
      };

      if (column.body) {
        def.cellRenderer = (params) => column.body(params.data);
        def.sortable = column.sortable ?? false;
      }

      if (column.valueGetter) {
        def.valueGetter = column.valueGetter;
      }

      if (column.cellClass) {
        def.cellClass = column.cellClass;
      }

      if (column.cellClassRules) {
        def.cellClassRules = column.cellClassRules;
      }

      if (column.cellStyle) {
        def.cellStyle = column.cellStyle;
      }

      if (column.headerClass) {
        def.headerClass = column.headerClass;
      }

      if (column.flex !== undefined) {
        def.flex = column.flex;
      } else if (size.width || size.minWidth || size.maxWidth) {
        def.flex = 0;
      }

      if (column.maxWidth) {
        def.maxWidth = column.maxWidth;
      }

      if (column.minWidth) {
        def.minWidth = column.minWidth;
      }

      if (column.resizable === false) {
        def.resizable = false;
      }

      return def;
    });

    if (showSelectionColumn) {
      defs.unshift({
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: false,
        width: selectionColumnWidth,
        minWidth: selectionColumnWidth,
        maxWidth: selectionColumnWidth,
        sortable: false,
        suppressMenu: true,
      });
    }

    return defs;
  }, [columns, showSelectionColumn, selectionColumnWidth]);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 120,
      resizable: true,
    }),
    []
  );

  const gridStyle = {
    height: height ?? "100%",
    width: "100%",
    ...style,
  };

  return (
    <div className={`ag-theme-alpine ${className ?? ""}`} style={gridStyle}>
      <AgGridReact
        rowData={rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowSelection={rowSelection}
        rowHeight={rowHeight}
        suppressRowClickSelection={false}
        suppressRowHoverHighlight={false}
        onSelectionChanged={onSelectionChanged}
        isRowSelectable={isRowSelectable}
        getRowId={getRowId}
        getRowClass={getRowClass}
        onRowClicked={onRowClicked}
        animateRows={false}
      />
    </div>
  );
};

export default AnDataGrid;
